import { useEffect, useState } from 'react'
import { Moon } from 'lucide-react'

/**
 * Warns when a task's deadline reminders would start inside the user's quiet
 * hours — and would therefore be silently suppressed.
 *
 * Owner decision 2026-08-30: a deadline nag RESPECTS quiet hours rather than
 * overriding them, but the user is told when they set the time. Without this
 * a task due at 03:00 arms at 02:05, every firing is logged as suppressed,
 * and the reminder silently fails at exactly the deadline it exists to
 * protect. Saying so at capture is the difference between a considered
 * default and a broken feature.
 *
 * Compares plain local HH:MM against the profile's own sleep/wake strings —
 * the same clock the user just typed into, so there is no timezone maths here
 * and nothing to get wrong.
 */
type QuietHours = { sleep: string; wake: string } | null

function toMinutes(hhmm: string): number | null {
  const m = /^(\d{1,2}):(\d{2})/.exec(String(hhmm || ''))
  if (!m) return null
  return Number(m[1]) * 60 + Number(m[2])
}

/** Quiet windows wrap midnight (22:00 → 07:00), so this is not a simple range. */
function insideQuiet(minute: number, q: { sleep: string; wake: string }): boolean {
  const s = toMinutes(q.sleep)
  const w = toMinutes(q.wake)
  if (s == null || w == null) return false
  return s <= w ? minute >= s && minute < w : minute >= s || minute < w
}

export function QuietHoursNotice({
  dueTime,
  leadMinutes,
}: {
  dueTime?: string | null
  leadMinutes?: number | string | null
}) {
  const [quiet, setQuiet] = useState<QuietHours>(null)
  const [loaded, setLoaded] = useState(false)

  useEffect(() => {
    let live = true
    fetch('/api/reminders/v1/quiet-window', { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((b) => {
        if (!live) return
        setQuiet(b?.quiet_hours ?? null)
        setLoaded(true)
      })
      .catch(() => live && setLoaded(true))
    return () => {
      live = false
    }
  }, [])

  const due = toMinutes(dueTime || '')
  if (!loaded || !quiet || due == null) return null

  const lead = Number(leadMinutes) > 0 ? Number(leadMinutes) : 55
  // Where the nagging would START. Negative wraps to the previous day.
  const start = ((due - lead) % 1440 + 1440) % 1440

  const startQuiet = insideQuiet(start, quiet)
  const dueQuiet = insideQuiet(due, quiet)
  if (!startQuiet && !dueQuiet) return null

  const hhmm = (m: number) =>
    `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`

  return (
    <div className="mt-2 flex items-start gap-2 rounded-lg border border-warn/40 bg-warn/10 px-3 py-2">
      <Moon className="mt-0.5 h-4 w-4 flex-shrink-0 text-warn" />
      <p className="text-xs text-secondary">
        {startQuiet && !dueQuiet ? (
          <>
            Reminders would start at <strong>{hhmm(start)}</strong>, inside your quiet
            hours ({quiet.sleep}–{quiet.wake}). They stay silent until{' '}
            <strong>{quiet.wake}</strong>, then continue as normal.
          </>
        ) : (
          <>
            This deadline falls inside your quiet hours ({quiet.sleep}–{quiet.wake}), so
            the reminders will not make a sound. Move the time, or change your quiet
            hours in Settings, if you need this one to wake you.
          </>
        )}
      </p>
    </div>
  )
}
